/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { TextField, Box, Autocomplete, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { createFilterOptions } from '@mui/material/Autocomplete';

import colors from '../../../constants/colors';

const $LABEL = 'Select option(s)';
const $FIXEDHEIGHT = 44;
const $FIXEDWIDTH = 250;

// Unique ID counter so every Autocomplete instance gets its own id
let _idCounter = 0;
const getUniqueId = () => `custom-dropdown-${++_idCounter}`;

// Highlight the matched portion of text in bold/orange
const HighlightMatch = ({ text, query }) => {
  if (!query || !text) return <span>{text}</span>;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, index)}
      <span style={{ fontWeight: 700, color: colors.warning || '#e67e22' }}>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </span>
  );
};

const CustomDropdown = ({ title, value, handleChange, defaultProps, disabled, type, mandatory, selectedLabel, loading }) => {
  const useStyles = makeStyles(() => ({
    row: {
      display: 'block',
      margin: type === 'special' ? '0' : '6px 0'
    },
    autocompleteBox: {
      display: 'flex',
      alignItems: 'center',
    },
    autocompleteTitle: {
      fontSize: '1.4rem',
      marginBottom: '3px',
      fontStyle: 'normal',
      fontWeight: '500',
      color: '#263238',
    },
    iconMandatory: {
      color: colors.warning,
      paddingLeft: '0.25em',
      fontFamily: 'Roboto, RobotoDraft',
      fontSize: '1.6rem',
      lineHeight: 1,
    },
    autocomplete: {
      background: disabled ? colors.disabled : colors.white,
      width: '100%',
      minWidth: `${$FIXEDWIDTH}px`,
      '& .MuiOutlinedInput-root': {
        height: '36px',
        padding: '0 8px !important',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D5D5D5',
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#aaaaaa',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1a5fa8',
        borderWidth: '1.5px',
      },
      '& .MuiAutocomplete-endAdornment': {
        position: 'absolute',
        right: '6px !important',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
      },
      '& .MuiAutocomplete-endAdornment button': {
        padding: '2px',
      },
      '& .MuiAutocomplete-clearIndicator': {
        visibility: 'visible',
        fontSize: '18px',
      },
      '& .MuiAutocomplete-popupIndicator': {
        fontSize: '18px',
      },
    },
    textfield: {
      '& .MuiInputBase-input.MuiAutocomplete-input': {
        fontSize: '1.2rem',
        padding: '4px 8px !important',
      },
      '& .MuiInputLabel-root': {
        display: 'none',
      },
    }
  }));
  const classes = useStyles('');

  // Stable unique id per component instance
  const instanceId = useRef(getUniqueId()).current;

  const [requiredField, setRequiredField] = useState(true);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState($LABEL);
  const [inputValue, setInputValue] = useState('');

  const handleSelectedChange = (_, item) => {
    handleChange(item);
  };

  useEffect(() => {
    if (selectedLabel) {
      setDefaultPlaceholder(selectedLabel);
    } else {
      setDefaultPlaceholder($LABEL);
    }
  }, [selectedLabel]);

  useEffect(() => {
    if (!value) {
      setRequiredField(true);
    } else {
      setRequiredField(false);
    }
  }, [value]);

  if (loading) return (
    <>
      <Skeleton width={$FIXEDWIDTH} />
      <Skeleton width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
    </>
  );

  // Deduplicate options by label
  const rawOptions = defaultProps.options || [];
  const getLabel = defaultProps.getOptionLabel || ((o) => (typeof o === 'string' ? o : o.DescriptionShort || ''));

  const seen = new Set();
  const dedupedOptions = rawOptions.filter((opt) => {
    const label = getLabel(opt);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });

  // filterOptions scoped to this instance's getLabel — no cross-field leakage
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => getLabel(option),
    trim: true,
  });

  return (
    <>
      <Box className={classes.row}>
        <div className={classes.autocompleteBox}>
          <div className={classes.autocompleteTitle}>{title}</div>
          {mandatory && requiredField && <span className={classes.iconMandatory}>*</span>}
        </div>
        <Autocomplete
          id={instanceId}
          value={value || null}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          onChange={handleSelectedChange}
          options={dedupedOptions}
          getOptionLabel={getLabel}
          filterOptions={filterOptions}
          isOptionEqualToValue={(option, val) => {
            if (!option || !val) return false;
            return getLabel(option) === getLabel(val);
          }}
          renderOption={(props, option) => (
            <li {...props}>
              <HighlightMatch text={getLabel(option)} query={inputValue} />
            </li>
          )}
          renderInput={(params) => {
            const { InputLabelProps, ...restParams } = params;
            return (
              <TextField
                {...restParams}
                className={classes.textfield}
                placeholder={defaultPlaceholder + (mandatory && requiredField ? '*' : '')}
                required={mandatory}
                variant='outlined'
                InputLabelProps={{ shrink: false }}
                label=''
              />
            );
          }}
          className={classes.autocomplete}
          disabled={disabled}
        />
      </Box>
    </>
  );
};

CustomDropdown.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.any,
  handleChange: PropTypes.func.isRequired,
  defaultProps: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  mandatory: PropTypes.bool,
  selectedLabel: PropTypes.string,
  loading: PropTypes.bool
};

CustomDropdown.defaultProps = {
  value: null,
  disabled: false,
  type: null,
  mandatory: false,
  selectedLabel: null,
  loading: false
};

export default CustomDropdown;
